import { supabase } from "./supabase";

export type SeoSeriesPoint = { day: string; views: number; visitors: number };
export type SeoNamedCount = { name: string; value: number };

export type SeoSummary = {
  setupRequired?: boolean;
  days: number;
  views: number;
  visitors: number;
  views7: number;
  visitors7: number;
  viewsPrev7: number;
  leads: number;
  leads7: number;
  organicShare: number;
  series: SeoSeriesPoint[];
  topPages: SeoNamedCount[];
  topReferrers: SeoNamedCount[];
  devices: SeoNamedCount[];
  locales: SeoNamedCount[];
  landings: Array<{ name: string; url: string; views: number }>;
};

const COLLECT_URL = "/api/site-analytics";
const VISITOR_KEY = "on45d_vid";

function visitorId() {
  try {
    const existing = sessionStorage.getItem(VISITOR_KEY);
    if (existing) return existing;
    const id = crypto.randomUUID();
    sessionStorage.setItem(VISITOR_KEY, id);
    return id;
  } catch {
    return "anon";
  }
}

export function isPublicAnalyticsPath(pathname: string) {
  return (
    pathname === "/" ||
    pathname === "/blog" ||
    pathname === "/contact" ||
    pathname === "/solutions" ||
    pathname.startsWith("/blog/") ||
    pathname.startsWith("/solutions/") ||
    pathname.startsWith("/landing/")
  );
}

export function reportPageView(pathname: string, locale?: string) {
  if (typeof window === "undefined") return;
  if (!isPublicAnalyticsPath(pathname)) return;
  const host = window.location.hostname;
  if (host === "localhost" || host === "127.0.0.1") return;

  const payload = JSON.stringify({
    path: pathname,
    referrer: document.referrer || "",
    locale: locale === "en" ? "en" : "es",
    visitorId: visitorId(),
  });

  try {
    const blob = new Blob([payload], { type: "application/json" });
    if (navigator.sendBeacon?.(COLLECT_URL, blob)) return;
  } catch {
    // fall through to fetch
  }

  void fetch(COLLECT_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: payload,
    keepalive: true,
  }).catch(() => undefined);
}

export async function fetchSeoSummary(days = 28): Promise<SeoSummary> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error("Authentication required");

  const response = await fetch(`${COLLECT_URL}?days=${days}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(payload.error || "Could not load site analytics");
  }
  return payload as SeoSummary;
}

export function viewsDeltaPct(current: number, previous: number) {
  if (!previous && !current) return 0;
  if (!previous) return 100;
  return Math.round(((current - previous) / previous) * 100);
}
